import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, XCircle, Loader2, ArrowRight, 
  FileText, MessageSquare, RefreshCw, Home
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';

const PressPaymentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [press, setPress] = useState(null);
  
  const status = searchParams.get('status') || searchParams.get('payment');
  const contractId = searchParams.get('contract') || searchParams.get('external_reference');
  const isSuccess = status === 'success' || status === 'approved';
  const isPending = status === 'pending';
  const isFailure = status === 'failure' || status === 'rejected';

  useEffect(() => {
    const fetchContractData = async () => {
      if (!contractId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get(`/press/contracts/${contractId}`);
        setContract(response.data);
        
        // Fetch press info for logo
        if (response.data?.press_id) {
          try {
            const pressRes = await api.get(`/press/public/${response.data.press_id}`);
            setPress(pressRes.data);
          } catch (e) {
            console.log('Could not fetch press info');
          }
        }
      } catch (error) {
        console.error('Error fetching contract:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContractData();
  }, [contractId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="animate-spin text-yellow-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full"
      >
        {/* Logo del Medio */}
        {press?.logo_url && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-6"
          >
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-zinc-800 border-2 border-zinc-700">
              <img 
                src={press.logo_url} 
                alt={press.media_name} 
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        )}

        <div className={`bg-zinc-900/80 backdrop-blur-xl border rounded-3xl p-8 text-center ${
          isSuccess ? 'border-green-500/30' : isPending ? 'border-yellow-500/30' : 'border-red-500/30'
        }`}>
          {/* Status Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
              isSuccess ? 'bg-green-500/20' : isPending ? 'bg-yellow-500/20' : 'bg-red-500/20'
            }`}
          >
            {isSuccess ? (
              <CheckCircle className="text-green-400" size={48} />
            ) : isPending ? (
              <Loader2 className="text-yellow-400 animate-spin" size={48} />
            ) : (
              <XCircle className="text-red-400" size={48} />
            )}
          </motion.div>

          {/* Title */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`text-2xl font-bold mb-3 ${
              isSuccess ? 'text-green-400' : isPending ? 'text-yellow-400' : 'text-red-400'
            }`}
          >
            {isSuccess ? '¡Pago Exitoso!' : isPending ? 'Pago Pendiente' : 'Pago No Completado'}
          </motion.h1>

          {/* Description */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-zinc-400 mb-6"
          >
            {isSuccess ? (
              <>
                Tu contratación con <span className="text-white font-semibold">{press?.media_name || 'el medio'}</span> ha sido confirmada.
                {contract?.event_name && (
                  <span className="block mt-1 text-sm">
                    Evento: <span className="text-yellow-400">{contract.event_name}</span>
                  </span>
                )}
              </>
            ) : isPending ? (
              <>
                Tu pago está siendo procesado. Te notificaremos cuando se confirme.
              </>
            ) : (
              <>
                No pudimos procesar tu pago. Podés intentarlo nuevamente desde tu panel.
              </>
            )}
          </motion.p>

          {/* Contract Info */}
          {contract && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-zinc-800/50 rounded-xl p-4 mb-6 text-left"
            >
              <div className="flex justify-between text-sm mb-2">
                <span className="text-zinc-500">Contrato:</span>
                <span className="text-white font-mono">#{contractId?.slice(-8).toUpperCase()}</span>
              </div>
              {contract.total_amount && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-500">Monto total:</span>
                  <span className="text-green-400 font-bold">${contract.total_amount.toLocaleString()} ARS</span>
                </div>
              )}
              {contract.quote_amount && (
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Servicio contratado:</span>
                  <span className="text-white">${contract.quote_amount.toLocaleString()} ARS</span>
                </div>
              )}
            </motion.div>
          )}

          {/* Actions */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            {isSuccess ? (
              <>
                <Button 
                  onClick={() => navigate('/producer/press?tab=contracts')}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  <FileText size={18} className="mr-2" />
                  Ver Mis Contratos
                </Button>
                {contract?.status === 'in_progress' && (
                  <Button 
                    onClick={() => navigate(`/producer/press?tab=contracts&chat=${contractId}`)}
                    variant="outline"
                    className="w-full border-zinc-700"
                  >
                    <MessageSquare size={18} className="mr-2" />
                    Ir al Chat del Contrato
                  </Button>
                )}
              </>
            ) : isPending ? (
              <Button 
                onClick={() => navigate('/producer/press?tab=contracts')}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <FileText size={18} className="mr-2" />
                Ver Estado del Contrato
              </Button>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/producer/press?tab=contracts')}
                  className="w-full bg-red-500 hover:bg-red-600"
                >
                  <RefreshCw size={18} className="mr-2" />
                  Reintentar Pago
                </Button>
                <Button 
                  onClick={() => navigate('/producer/press')}
                  variant="outline"
                  className="w-full border-zinc-700"
                >
                  <Home size={18} className="mr-2" />
                  Volver al Portal de Prensa
                </Button>
              </>
            )}
          </motion.div>

          {/* Footer */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-zinc-600 text-xs mt-6"
          >
            {isSuccess 
              ? 'Recibirás un email de confirmación con los detalles del contrato.' 
              : isPending
              ? 'El pago puede demorar unos minutos en procesarse.'
              : 'Si el problema persiste, contactá a soporte.'
            }
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
};

export default PressPaymentResultPage;
